import { useNavigate } from "react-router-dom";
import { FetchedReportData, ReportStatusType } from "../../../types";
import devLogger from "../../../utils/devLogger";
import { getUserRole } from "../../../utils/authHelpers";
import { formatDate } from "../../../utils/dateUtils";
import { capitalizeFirstLetters } from "../../../utils/stringUtils";
import { reportStatusStyles } from "../../SubmitTip/resources";
import TruncatedText from "../../common/TruncatedText";
import { FaStar, FaX } from "react-icons/fa6";
import radioChecked from "../../../images/radio-checked-svg.svg";
import radioUnchecked from "../../../images/radio-unchecked-svg.svg";

// Function to render the special authority marker if applicable
const SpecialAuthorityMarker = ({
  hasSpecialAuthority,
}: {
  hasSpecialAuthority?: boolean;
}) => {
  if (!hasSpecialAuthority) return null;
  return (
    <div className="absolute top-0 left-0 bg-danger text-white text-[12px] font-[700] h-[16px] w-[16px] rounded-tl-[9px] flex justify-center items-center z-10">
      <FaStar />
    </div>
  );
};

const ReportStatusPill = ({
  reportStatus,
}: {
  reportStatus: ReportStatusType;
}) => {
  return (
    <div className="relative flex flex-col items-start justify-start gap-1 w-full">
      <div className="flex gap-2 items-start">
        <div className="flex items-center gap-1.5 w-full">
          <span
            className={`rounded-3xl py-1 px-2 sm:px-3 font-semibold text-[8px] sm:text-[10px] leading-3 ${
              reportStatusStyles[reportStatus || "submitted"]
            }`}
          >
            {reportStatus.toUpperCase()}
          </span>
        </div>
      </div>
    </div>
  );
};

const CaseReportCard = ({
  report,
  selected = false,
  selectable = true,
  onSelect,
  onDelete,
  setReportDetails,
}: CaseReportCardProps) => {
  const navigate = useNavigate();
  if (!report || !report._id) return null;

  const { city, county, state } = report.incidentDescription.detailedLocation;
  const isSuperAdmin = getUserRole() === "super_admin";

  const handleViewreport = () => {
    devLogger.log("🚀 ~ handleViewreport ~ report:", report);

    setReportDetails(report);
    navigate(`/admin/report/${report._id}`);
  };

  return (
    <div
      className="flex items-center gap-3"
      onClick={!selectable ? handleViewreport : undefined}
    >
      {selectable && (
        <img
          onClick={selectable ? onSelect : undefined}
          src={selected ? radioChecked : radioUnchecked}
          alt="Select"
          className="cursor-pointer"
        />
      )}

      <div
        className={`relative flex flex-col w-full gap-5 border-[3px] border-gray-7 rounded-xl cursor-pointer group ${
          selectable && selected
            ? "bg-black text-gray-1"
            : "bg-white text-gray-7"
        } transition-opacity duration-200`}
        onClick={selectable ? onSelect : undefined}
      >
        {/* Card Content */}
        <div className="flex flex-col gap-5 px-4 sm:px-6 py-3 z-10">
          {/* Special Authority Marker */}
          <SpecialAuthorityMarker
            hasSpecialAuthority={report.abuserDetails?.hasSpecialAuthority}
          />

          <div className="flex justify-between w-full gap-x-2">
            <p className="font-bold text-base leading-5 sm:text-[20px] sm:leading-[26px] max-w-[340px]">
              N<sup>o</sup>{" "}
              <TruncatedText
                text={report._id}
                textClasses="text-gray-7 text-base"
              />
            </p>

            <div className="relative">
              {/* Date is hidden on hover for larger screens */}
              <p
                className={`font-semibold text-sm sm:text-lg leading-6 ${
                  isSuperAdmin ? "hidden group-hover:hidden" : "hidden sm:block"
                } sm:block`}
              >
                Date: {formatDate(report.incidentDescription.date, "mmddyyyy")}
              </p>

              {/* Close button always visible on small screens */}
              {isSuperAdmin && (
                <FaX
                  size={16}
                  className="block sm:hidden group-hover:block text-gray-6 text-[20px] cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(report._id);
                  }}
                />
              )}
            </div>
          </div>

          <div className="flex justify-between items-end gap-1 w-full">
            <div className="flex flex-col w-auto flex-1">
              <p className="font-extrabold text-sm leading-4 sm:text-base sm:leading-5">
                {report.caseName.toUpperCase()}
              </p>
              <p className="font-normal text-xs leading-4 sm:text-sm sm:leading-[18px]">
                {capitalizeFirstLetters(
                  [city, county, state].filter(Boolean).join(" ")
                )}
              </p>
              <p className="font-normal text-xs leading-4 sm:text-sm sm:leading-[18px] mt-1">
                <strong>Reported:</strong>{" "}
                {formatDate(report.createdAt, "mmddyyyy")}
              </p>

              {/* Only show here on mobile */}
              <p className="font-normal text-xs leading-4 sm:text-sm sm:leading-[18px] mt-1 block sm:hidden">
                <strong>Incident Date:</strong>{" "}
                {formatDate(report.incidentDescription.date, "mmddyyyy")}
              </p>
            </div>

            <div className=" z-10">
              <ReportStatusPill reportStatus={report.status} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseReportCard;

interface CaseReportCardProps {
  report: FetchedReportData;
  selected: boolean;
  selectable: boolean;
  onSelect: () => void;
  onDelete: (id: string) => void;
  setReportDetails: (reportDetails: FetchedReportData | undefined) => void;
}
